'use client';
import { useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

type ContentTable = 'movies' | 'series';
type ChangeEvent = 'INSERT' | 'UPDATE' | 'DELETE';

interface RealtimeContentOptions {
  tables: ContentTable[];
  onInsert?: (table: ContentTable, record: Record<string, unknown>) => void;
  onUpdate?: (table: ContentTable, record: Record<string, unknown>) => void;
  onDelete?: (table: ContentTable, record: Record<string, unknown>) => void;
  onAnyChange?: (table: ContentTable, event: ChangeEvent, record: Record<string, unknown>) => void;
}

export function useRealtimeContent({
  tables,
  onInsert,
  onUpdate,
  onDelete,
  onAnyChange,
}: RealtimeContentOptions) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const callbacksRef = useRef({ onInsert, onUpdate, onDelete, onAnyChange });

  // Keep callbacks ref up to date without re-subscribing
  useEffect(() => {
    callbacksRef.current = { onInsert, onUpdate, onDelete, onAnyChange };
  });

  useEffect(() => {
    const supabase = createClient();
    const channelName = `realtime-content-${tables.join('-')}`;

    // Remove any existing channel with this name before creating a new one
    const existingChannel = supabase.getChannels().find((ch) => ch.topic === `realtime:${channelName}`);
    if (existingChannel) {
      supabase.removeChannel(existingChannel);
    }

    let channel = supabase.channel(channelName);

    // Register all listeners BEFORE calling subscribe()
    tables.forEach((table) => {
      channel = channel
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table },
          (payload) => {
            const record = payload.new as Record<string, unknown>;
            callbacksRef.current.onInsert?.(table, record);
            callbacksRef.current.onAnyChange?.(table, 'INSERT', record);
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table },
          (payload) => {
            const record = payload.new as Record<string, unknown>;
            callbacksRef.current.onUpdate?.(table, record);
            callbacksRef.current.onAnyChange?.(table, 'UPDATE', record);
          }
        )
        .on(
          'postgres_changes',
          { event: 'DELETE', schema: 'public', table },
          (payload) => {
            const record = payload.old as Record<string, unknown>;
            callbacksRef.current.onDelete?.(table, record);
            callbacksRef.current.onAnyChange?.(table, 'DELETE', record);
          }
        );
    });

    // Subscribe only after all listeners are registered
    channel.subscribe();
    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables.join(',')]);
}
